import EventEmitter from "events";
import { Client, ClientConfigType, ClientEvent } from "~/modules/client";
import { Role } from "~/modules/auth";
import { Twilsock, TwilsockEvent } from "~/modules/websocket";
import { Session, SessionEvent } from "~/modules/session";
import { getLogger, Logger, LoggerName } from "~/modules/logger";
import {
    TelemetrySdkEvent,
    TelemetrySdkEventGroup,
    TelemetrySdkClient,
    TelemetrySdkEventName,
    TelemetrySdkEventSource
} from "~/modules/telemetrySdkClient";
import { Emitter, proxyEvent } from "~/modules/events";
import { getTelemetrySdkClient } from "~/modules/telemetrySdkClient/telemetrySdkClient";
import { ProfileConnectorApis } from "~/modules/profileConnector/Definitions";
import { ProfileConnectorImpl } from "~/modules/profileConnector/ProfileConnectorImpl";
import { ContextManager } from "~/modules/contextManager/ContextManager";
import { SessionImpl } from "~/modules/session/Session/SessionImpl";
import { TwilsockImpl } from "~/modules/websocket/Twilsock/TwilsockImpl";
import { AccountConfigStore } from "~/modules/config/AccountConfig/AccountConfigImpl/AccountConfigStore/AccountConfigStore";
import { VirtualAgentDataApi } from "~/modules/virtualAgentData/Definitions";
import { VirtualAgentDataImpl } from "~/modules/virtualAgentData/VirtualAgentDataImpl";
import { ActionsImpl } from "~/modules/actions/ActionsImpl";
import type { Actions } from "~/modules/actions/Actions";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import type { TaskRouter } from "~/packages/taskrouter/TaskRouter";
import type { Cbm } from "~/packages/cbm/Cbm";
import type { VoiceController } from "~/modules/voice/VoiceController";
import { CbmImpl } from "~/packages/cbm/CbmImpl";
import { VoiceControllerImpl } from "~/modules/voice/VoiceControllerImpl";
import { assertNotEmptyString } from "~/utils/assert";

export class ClientImpl implements Client {
    readonly #session: Session;

    readonly #connection: Twilsock;

    readonly #logger: Logger;

    public readonly config: ClientConfigType;

    readonly #ctx: ContextManager;

    readonly #telemetrySdkClient: TelemetrySdkClient;

    readonly #emitter: Emitter;

    readonly #virtualAgentDataApi: VirtualAgentDataApi;

    readonly #actions: Actions;

    readonly #taskRouter: TaskRouter;

    readonly #profileConnector: ProfileConnectorApis;

    readonly #cbm: Cbm;

    readonly #voiceController: VoiceController;

    #isDestroyed: boolean;

    constructor(ctx: ContextManager) {
        this.#session = ctx.getInstanceOf(SessionImpl);
        this.#connection = ctx.getInstanceOf(TwilsockImpl);
        this.config = {
            account: ctx.getInstanceOf(AccountConfigStore)
        };
        this.#telemetrySdkClient = getTelemetrySdkClient(ctx);
        this.#logger = getLogger(LoggerName.Client);
        this.#emitter = new EventEmitter();
        this.#virtualAgentDataApi = ctx.getInstanceOf(VirtualAgentDataImpl);
        this.#profileConnector = ctx.getInstanceOf(ProfileConnectorImpl);
        this.#actions = ctx.getInstanceOf(ActionsImpl);
        this.#taskRouter = ctx.getInstanceOf(TaskRouterImpl);
        this.#cbm = ctx.getInstanceOf(CbmImpl);
        this.#voiceController = ctx.getInstanceOf(VoiceControllerImpl);
        this.#ctx = ctx;

        this.setupProxies();
    }

    setupProxies(): void {
        proxyEvent(this.#connection, this.#emitter, TwilsockEvent.TokenAboutToExpire, ClientEvent.TokenAboutToExpire);
        proxyEvent(this.#connection, this.#emitter, TwilsockEvent.TokenExpired, ClientEvent.TokenExpired);
        proxyEvent(this.#connection, this.#emitter, TwilsockEvent.TokenUpdated, ClientEvent.TokenUpdated);
        proxyEvent(this.#connection, this.#emitter, TwilsockEvent.ConnectionError, ClientEvent.ConnectionLost);
        proxyEvent(this.#connection, this.#emitter, TwilsockEvent.Connected, ClientEvent.ConnectionRestored);
        proxyEvent(this.#connection, this.#emitter, TwilsockEvent.Disconnected, ClientEvent.Disconnected);
        proxyEvent(this.#session, this.#emitter, SessionEvent.TokenAutoUpdateFailed, ClientEvent.TokenAutoUpdateFailed);
        proxyEvent(
            this.#session,
            this.#emitter,
            SessionEvent.TokenMaxLifetimeReached,
            ClientEvent.TokenMaxLifetimeReached
        );
    }

    async updateToken(token: string): Promise<void> {
        assertNotEmptyString(token, "token");
        try {
            await this.#session.updateToken(token);
        } catch (e) {
            this.#logger.error(e?.source, e);
        }

        
        
        
        
        

        
        
        
        
        

        
        
        
        
        
    }

    #sendDestroyEvent = async (): Promise<void> => {
        try {
            const telemetrySdkClient = this.#telemetrySdkClient;
            const group = telemetrySdkClient.createEventGroup<TelemetrySdkEvent>(TelemetrySdkEventGroup.Default);
            await group.addEvents({
                eventName: TelemetrySdkEventName.ClientDestroyed,
                eventSource: TelemetrySdkEventSource.Client
            });
        } catch (e) {
            this.#logger.error("Failed to send telemetry destroy event", e);
        }
    };

    async destroy(): Promise<void> {
        this.#logger.debug("Client destroy called");
        if (this.#isDestroyed) {
            return;
        }

        this.#isDestroyed = true;
        await this.#sendDestroyEvent();
        await this.#session.destroy();
        this.#emitter.removeAllListeners();
        this.#ctx.destroy();
        this.#logger.debug("Client has been destroyed");
    }

    get roles(): Array<Role> {
        return [...this.#session.roles];
    }

    get token(): string {
        return this.#session.token;
    }

    get profileConnector(): ProfileConnectorApis {
        return this.#profileConnector;
    }

    get virtualAgentData(): VirtualAgentDataApi {
        return this.#virtualAgentDataApi;
    }

    get actions(): Actions {
        return this.#actions;
    }

    addListener(eventName: ClientEvent, listener: (...args: unknown[]) => void): this {
        this.#emitter.on(eventName, listener);
        return this;
    }

    removeListener(eventName: ClientEvent, listener: (...args: unknown[]) => void): this {
        this.#emitter.removeListener(eventName, listener);
        return this;
    }
}
